import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRefresh } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { faExclamationTriangle, faPlus } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Button } from "@/components/ui/button/Button";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  useOpenInNewWindowMenu,
  useOpenUrl,
  useOsNavigate,
  useSetWindowTitle,
} from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import type { FaLeadListItem } from "@/services/api/agentic-v2";
import { HomeListTable } from "@/components/home/HomeListTable";
import {
  HOME_SURFACE_TABLE_HEAD_BORDER,
  HomeSurfaceTableBody,
  HomeSurfaceTableCell,
  HomeSurfaceTableHead,
  HomeSurfaceTableHeaderCell,
  HomeSurfaceTableSortHeader,
} from "@/components/home/HomeSurfaceTable";
import { HOME_MUTED_TEXT_CLASS } from "@/components/home/homeSurfaces";
import { CreateLeadDialog } from "@/components/leads/CreateLeadDialog";
import { LeadsCategoryFilter } from "@/components/leads/LeadsCategoryFilter";
import { LossReportDialog } from "@/components/leads/LossReportDialog";
import {
  DEFAULT_LEADS_FILTERS,
  LEADS_FETCH_LIMIT,
  useLeadStatuses,
  useLeadsPage,
  type LeadsFilters,
} from "@/services/api/agentic-v2/queries/useLeadsPage";
import { buildLeadDetailWindowUrl } from "@/navigation/apps/leadDetailLocation";
import { useAgentListFocus } from "@/hooks/useAgentListFocus";

type SortColumn = "lead" | "value" | "status" | "updated";
type SortDirection = "asc" | "desc";

const DEFAULT_SORT: { column: SortColumn; direction: SortDirection } = {
  column: "updated",
  direction: "desc",
};

function formatUpdatedAt(value: string | null | undefined): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatOpportunityValue(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function sortValueForColumn(lead: FaLeadListItem, column: SortColumn): string | number | null {
  switch (column) {
    case "lead":
      return lead.display_name;
    case "value":
      return lead.opportunity_value ?? null;
    case "status":
      return lead.lead_status || null;
    case "updated": {
      if (!lead.updated_at) return null;
      const timestamp = new Date(lead.updated_at).getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    }
    default:
      return null;
  }
}

function compareSortValues(left: string | number | null, right: string | number | null): number {
  const leftEmpty = left === null || left === "";
  const rightEmpty = right === null || right === "";
  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, {
    sensitivity: "base",
    numeric: true,
  });
}

function sortLeads(
  leads: FaLeadListItem[],
  sort: { column: SortColumn; direction: SortDirection },
): FaLeadListItem[] {
  const sorted = [...leads].sort((left, right) => {
    const comparison = compareSortValues(
      sortValueForColumn(left, sort.column),
      sortValueForColumn(right, sort.column),
    );
    if (comparison !== 0) return comparison;
    return compareSortValues(left.display_name, right.display_name);
  });
  return sort.direction === "desc" ? sorted.reverse() : sorted;
}

function cellText(value: string | null | undefined): string {
  return value?.trim() || "—";
}

export interface LeadsListProps {
  /** When nested inside another surface — skip outer page chrome. */
  embedded?: boolean;
}

export function LeadsList({ embedded = false }: LeadsListProps = {}) {
  const setWindowTitle = useSetWindowTitle();
  const titleSetRef = useRef(false);
  useEffect(() => {
    if (embedded || titleSetRef.current) return;
    setWindowTitle("Leads");
    titleSetRef.current = true;
  }, [setWindowTitle, embedded]);

  const osNavigate = useOsNavigate();
  const openUrl = useOpenUrl();
  const newWindowMenu = useOpenInNewWindowMenu();

  const [filters, setFilters] = useState<LeadsFilters>(DEFAULT_LEADS_FILTERS);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [createOpen, setCreateOpen] = useState(false);
  const [lossReportOpen, setLossReportOpen] = useState(false);

  const { leads, loading, refetching, error, hasMore, refresh } = useLeadsPage({ filters });
  const statusesQuery = useLeadStatuses({});

  const sortedLeads = useMemo(() => sortLeads(leads, sort), [leads, sort]);
  useAgentListFocus({
    focusPrefix: "lead",
    rows: sortedLeads,
    getRowId: (row) => row._id,
    enabled: true,
  });

  const toggleSort = (column: SortColumn) => {
    setSort((current) =>
      current.column === column
        ? { column, direction: current.direction === "asc" ? "desc" : "asc" }
        : { column, direction: "asc" },
    );
  };

  const navigateToLead = (lead: FaLeadListItem) => {
    const path = buildLeadDetailWindowUrl(lead._id);
    if (openUrl(path, { title: lead.display_name })) return;
    osNavigate(path);
  };

  const toolbarTrailing = (
    <div className="flex items-center gap-2">
      <LeadsCategoryFilter
        value={filters.status}
        onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
        filteredCount={sortedLeads.length}
      />
      <Button
        plain
        size="sm"
        onClick={() => {
          refresh();
          void statusesQuery.refresh();
        }}
        loading={refetching}
        aria-label="Refresh leads"
        data-testid="leads-refresh"
      >
        <FontAwesomeIcon icon={faRefresh} />
      </Button>
      <Button outline size="sm" onClick={() => setLossReportOpen(true)} data-testid="leads-loss-report-button">
        Loss report
      </Button>
      <Button
        color="indigo"
        size="sm"
        onClick={() => setCreateOpen(true)}
        data-testid="leads-create-button"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-1.5 h-3 w-3" />
        Create lead
      </Button>
    </div>
  );

  const listContent = (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        {loading ? (
          <div className="space-y-2">
            <LoadingPlaceholder height="72px" />
            <LoadingPlaceholder height="72px" />
            <LoadingPlaceholder height="72px" />
          </div>
        ) : error ? (
          <ErrorState
            message={error.message}
            onRetry={() => {
              refresh();
              void statusesQuery.refresh();
            }}
          />
        ) : (
          <HomeListTable
            testId="leads-table"
            searchQuery={filters.q}
            onSearchQueryChange={(q) => setFilters((prev) => ({ ...prev, q }))}
            searchPlaceholder="Search leads…"
            showSearch
            toolbarTrailing={toolbarTrailing}
            tableMinWidth="72rem"
            scrollClassName="min-h-0 flex-1"
            empty={sortedLeads.length === 0}
            emptyMessage={
              filters.q.trim() || filters.status
                ? "No leads match your filters."
                : "No leads yet."
            }
            footer={
              <p className={clsx("text-xs", HOME_MUTED_TEXT_CLASS)}>
                Showing {sortedLeads.length} lead{sortedLeads.length === 1 ? "" : "s"}
                {hasMore ? ` (first ${LEADS_FETCH_LIMIT} — refine search to narrow)` : ""}
              </p>
            }
          >
            <colgroup>
              <col className="min-w-[11rem]" />
              <col className="min-w-[6.5rem]" />
              <col className="min-w-[13rem]" />
              <col className="min-w-[7rem]" />
              <col className="min-w-[6.5rem]" />
              <col className="min-w-[8.5rem]" />
              <col className="min-w-[9rem]" />
              <col className="min-w-[9.5rem]" />
            </colgroup>
            <HomeSurfaceTableHead sticky>
              <tr className={HOME_SURFACE_TABLE_HEAD_BORDER}>
                <HomeSurfaceTableSortHeader
                  label="Lead"
                  column="lead"
                  activeColumn={sort.column}
                  direction={sort.direction}
                  onSort={toggleSort}
                  testId="leads-sort-lead"
                />
                <HomeSurfaceTableHeaderCell label="Proposition" />
                <HomeSurfaceTableHeaderCell label="Contact" />
                <HomeSurfaceTableHeaderCell label="Source" />
                <HomeSurfaceTableSortHeader
                  label="Value"
                  column="value"
                  activeColumn={sort.column}
                  direction={sort.direction}
                  align="right"
                  onSort={toggleSort}
                  testId="leads-sort-value"
                />
                <HomeSurfaceTableSortHeader
                  label="Status"
                  column="status"
                  activeColumn={sort.column}
                  direction={sort.direction}
                  onSort={toggleSort}
                  testId="leads-sort-status"
                />
                <HomeSurfaceTableHeaderCell label="Adviser" />
                <HomeSurfaceTableSortHeader
                  label="Updated"
                  column="updated"
                  activeColumn={sort.column}
                  direction={sort.direction}
                  onSort={toggleSort}
                  testId="leads-sort-updated"
                />
              </tr>
            </HomeSurfaceTableHead>
            <HomeSurfaceTableBody>
              {sortedLeads.map((lead) => (
                <tr
                  key={lead._id}
                  data-testid={`leads-item-${lead._id}`}
                  onClick={() => navigateToLead(lead)}
                  onContextMenu={(event) =>
                    newWindowMenu.onContextMenu(
                      event,
                      buildLeadDetailWindowUrl(lead._id),
                      lead.display_name,
                    )
                  }
                  className="cursor-pointer"
                >
                  <HomeSurfaceTableCell variant="title">
                    <span className="block truncate" title={lead.display_name}>
                      {lead.display_name}
                    </span>
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="body">
                    <span className="block truncate" title={lead.proposition ?? undefined}>
                      {cellText(lead.proposition)}
                    </span>
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="body">
                    <span
                      className="block truncate"
                      title={lead.email ?? lead.phone ?? undefined}
                    >
                      {cellText(lead.email ?? lead.phone)}
                    </span>
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="body" className="capitalize">
                    {cellText(lead.lead_source)}
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="meta" align="right" className="tabular-nums">
                    {formatOpportunityValue(lead.opportunity_value)}
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="title">
                    <span className="block truncate" title={lead.lead_status ?? undefined}>
                      {cellText(lead.lead_status)}
                    </span>
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="body">
                    <span
                      className="block truncate"
                      title={lead.adviser_name ?? undefined}
                    >
                      {cellText(lead.adviser_name)}
                    </span>
                  </HomeSurfaceTableCell>
                  <HomeSurfaceTableCell variant="meta" className="whitespace-nowrap">
                    {formatUpdatedAt(lead.updated_at)}
                  </HomeSurfaceTableCell>
                </tr>
              ))}
            </HomeSurfaceTableBody>
          </HomeListTable>
        )}
      </div>
      {newWindowMenu.menu}
      {lossReportOpen ? <LossReportDialog open onClose={() => setLossReportOpen(false)} /> : null}
      <CreateLeadDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(result) => {
          refresh();
          void statusesQuery.refresh();
          const path = buildLeadDetailWindowUrl(result.fa_client_id);
          if (openUrl(path, { title: "Lead" })) return;
          osNavigate(path);
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="leads-list-embedded">
        {listContent}
      </div>
    );
  }

  return (
    <div
      className={clsx("flex h-full w-full flex-col p-4", WORKBENCH_ROOT_TEXT_CLASS)}
      data-testid="leads-list"
    >
      <div
        className={clsx(
          "flex min-h-0 flex-1 flex-col rounded-2xl px-6 py-5",
          WORKBENCH_SURFACE_CLASS,
        )}
      >
        {listContent}
      </div>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-slate-600 dark:text-slate-300">
      <FontAwesomeIcon icon={faExclamationTriangle} className="text-2xl text-amber-500" />
      <p className="text-sm">Failed to load leads: {message}</p>
      <Button onClick={onRetry}>Retry</Button>
    </div>
  );
}
